import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  advanceFeedback,
  dismissRelay,
  productFeedbackQuery,
  retryRelay,
  submitFeedback,
  type FeedbackKind,
  type FeedbackRequest,
  type FeedbackShot,
  type ProductFeedbackPage as Page,
  type RelayedRequest,
} from '../api/productFeedback'
import { newAlert } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'

/**
 * Feedbackul despre produs: `/feature_requests` si `/bug_reports`.
 *
 * Perechea veche: `Product_feedback::index($kind)` cu macheta
 * `views/version-3/feature_requests/index.php` si `public/js/feature_requests.js`.
 *
 * =============================================================================
 * O PAGINA, DOUA ADRESE
 * =============================================================================
 *
 * Aceeasi componenta pentru amandoua felurile - se citesc la fel si merg pe
 * acelasi drum. Doua pagini gemene ar fi ajuns sa se deosebeasca la a treia
 * schimbare. Ce se schimba cu felul sunt CUVINTELE, nu asezarea: vezi `WORDS`.
 *
 * Adresele raman cele din `routes.php`, litera cu litera: `/app/bug_reports` si
 * `/app/feature_requests`. Sunt doua din cele trei legaturi din SUBSOLUL
 * fiecarei pagini din produs (`ShellFooter`), deci un nume schimbat aici ar
 * rupe un link de pe fiecare ecran.
 *
 * =============================================================================
 * ACELEASI CLASE CA IN PORTAL
 * =============================================================================
 *
 * `public/css/feature_requests.css` se leaga cu `usePageStyles`, si markupul e
 * cel din macheta veche (`fr-item`, `fr-head`, `fr-detail`, `fr-rail`, ...).
 * Ecranul trebuie sa ARATE la fel, nu doar sa spuna la fel.
 *
 * =============================================================================
 * CE NU HOTARASTE PAGINA ASTA
 * =============================================================================
 *
 * Cine vede ce. `canOpen` pe fiecare rand, `canOperate` si `canWorkTickets` pe
 * pagina vin gata hotarate de la server, ca in portal. Pagina nu stie drepturile
 * omului si nu ascunde nimic singura.
 *
 * CITITUL POZEI DE CATRE MODEL n-a fost mutat inca: `aiReady` e fals, si atunci
 * zona de poze nu promite nicio sugestie. Pozele pleaca oricum cu cererea.
 */

type Words = {
  title: string
  note: string
  neu: string
  empty: string
  label: string
  hint: string
  detail: string
  holder: string
  icon: string
  tone: string
}

/** Cuvintele se schimba cu felul; asezarea, nu. */
const WORDS: Record<FeedbackKind, Words> = {
  bug: {
    title: 'Bug Reports',
    note: 'what is not working, reported by anyone - yours come first',
    neu: 'Report a bug',
    empty: 'Nobody has reported anything yet.',
    label: 'What is not working',
    hint: 'briefly, in one line',
    detail: 'What you did, what you expected, and what happened instead',
    holder: 'the steps, and what you saw on screen',
    icon: 'fa-bug',
    tone: 'red-intense',
  },
  feature: {
    title: 'Feature Requests',
    note: 'what anyone has asked to change in the app - yours come first',
    neu: 'New request',
    empty: 'Nobody has asked for anything yet.',
    label: 'What you would like to be able to do',
    hint: 'briefly, in one line',
    detail: 'Why, and how it should work',
    holder: 'what you do instead today, and what gets in the way',
    icon: 'fa-lightbulb',
    tone: 'blue-mauve',
  },
}

const MAX_IMAGES = 5
const MAX_BYTES = 8 * 1024 * 1024
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']

/**
 * O durata scrisa in unitatea ei.
 *
 * „0 days" era raspunsul pentru orice se rezolva in aceeasi zi - adica pentru
 * cele mai multe - si nu spunea nimic. Sub o ora se scriu minute, sub doua zile
 * ore, apoi zile. Transcrisa din `$odFrSpan` al machetei.
 */

/*
 * O POZA CARE NU SE POATE ARATA SPUNE DE CE, NU RAMANE UN DREPTUNGHI RUPT.
 *
 * Adresele fisierelor se construiesc din `DOWNLOAD_SERVER` - sau, cand
 * `UPDATE_FILES_URL` e plin, sunt link-uri semnate spre update. Cand setarea
 * lipseste, `DownloadLinks::ready()` e fals si fiecare adresa iese
 * SIRUL GOL - dinadins, ca sa nu se lege o adresa pe care serverul n-o
 * cunoaste. Pana aici e bine; ce nu era bine e ce se vedea: `<img src="">`,
 * adica o iconita de imagine stricata, fara un cuvant. Cineva care a atasat
 * doua capturi la un Feature Request vede doua patrate goale si nu poate sti
 * daca pozele s-au pierdut, daca n-are dreptul sa le vada, sau daca lipseste
 * o setare.
 *
 * Doua cazuri, si se spun separat, fiindca cer lucruri diferite:
 *
 *  - adresa lipseste cu totul: serverul spune ce setare lipseste
 *    (`shot.missing`, din `DownloadLinks::missingSetting`). E o setare, nu o
 *    pierdere de fisier - poza e la locul ei. Cu fisierele pe update nu se
 *    pomeneste DOWNLOAD_SERVER: acolo o adresa goala vine din numele fisierului.
 *  - adresa exista dar fisierul nu vine (404, sters, miniatura negenerata):
 *    se arata NUMELE fisierului, care e tot un fir de tras, si ramane legat.
 */
function Shot({ shot }: { shot: FeedbackShot }) {
  const [broke, setBroke] = useState(false)

  if (shot.thumbUrl === '') {
    return (
      <span className="fr-shot fr-shot-missing" title={shot.filename}>
        <span className="fr-shot-said">
          <i className="fas fa-image" aria-hidden="true"></i> {shot.filename}
          <em>
            {shot.missing
              ? `attachments are not reachable from this instance - ${shot.missing} is not set`
              : 'this picture cannot be linked from here - open the ticket to see it'}
          </em>
        </span>
      </span>
    )
  }

  if (broke) {
    return (
      <a className="fr-shot fr-shot-missing" href={shot.url} target="_blank" rel="noopener" title={shot.filename}>
        <span className="fr-shot-said">
          <i className="fas fa-image" aria-hidden="true"></i> {shot.filename}
          <em>the picture did not load - open it in a new tab</em>
        </span>
      </a>
    )
  }

  return (
    <a className="fr-shot" href={shot.url} target="_blank" rel="noopener" title={shot.filename}>
      <img src={shot.thumbUrl} alt="" loading="lazy" onError={() => setBroke(true)} />
    </a>
  )
}

function span(hours: number): string {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`
  if (hours < 48) return `${hours < 10 ? Math.round(hours * 10) / 10 : Math.round(hours)} h`
  const days = hours / 24
  return `${days < 10 ? Math.round(days * 10) / 10 : Math.round(days)} days`
}

/** `date('d.m.Y')` din macheta. Datele vin ca „Y-m-d H:i:s", ora serverului. */
function day(value: string | null): string {
  if (!value) return ''
  const [date] = value.split(' ')
  const [y, m, d] = (date ?? '').split('-')
  return y && m && d ? `${d}.${m}.${y}` : value
}

/** `date('d.m.Y H:i')`. */
function moment(value: string | null): string {
  if (!value) return ''
  const [date, time] = value.split(' ')
  const short = (time ?? '').slice(0, 5)
  return short === '' ? day(value) : `${day(date)} ${short}`
}

/** `convert_bytes()` din portal, pentru fisierele care nu sunt poze. */
function bytes(size: number): string {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${Math.round((size / 1024) * 10) / 10} KB`
  return `${Math.round((size / (1024 * 1024)) * 10) / 10} MB`
}

/** `mb_substr(..., 0, 220)` din macheta: cardul arata inceputul, panoul tot. */
function short(text: string): string {
  return text.length > 220 ? text.slice(0, 220) : text
}

export function FeatureRequestsPage() {
  return <ProductFeedbackPage kind="feature" />
}

export function BugReportsPage() {
  return <ProductFeedbackPage kind="bug" />
}

function ProductFeedbackPage({ kind }: { kind: FeedbackKind }) {
  const page = useQuery(productFeedbackQuery(kind))
  usePageStyles(['css/adm.css', 'css/feature_requests.css'], page.data?.version ?? '')

  if (!page.data) return <Spinner />

  return <Screen page={page.data} />
}

function Screen({ page }: { page: Page }) {
  const words = WORDS[page.kind]
  const client = useQueryClient()
  const [open, setOpen] = useState(false)

  const reload = useCallback(() => {
    void client.invalidateQueries({ queryKey: ['product_feedback', page.kind] })
  }, [client, page.kind])

  /*
   * Doua liste, nu una. Ce asteapta si ce s-a rezolvat se citesc altfel: pe
   * primele te uiti ca sa vezi daca ai ce adauga, pe celelalte ca sa vezi ce a
   * apucat echipa. Amestecate si sortate dupa data, cele terminate impingeau in
   * jos exact randurile care mai au nevoie de ceva.
   */
  const waiting = page.requests.filter((one) => !one.resolved && !one.closed)
  const done = page.requests.filter((one) => one.resolved || one.closed)

  return (
    <div id="content">
      <div className={`adm-page-head adm-tone-${words.tone}`}>
        <span className="adm-page-icon">
          <i className={`fas ${words.icon}`}></i>
        </span>
        <div className="adm-page-title">
          <div className="adm-page-title-line">
            <h3>{words.title}</h3>
          </div>
          <span className="adm-page-note">{words.note}</span>
        </div>
        <div className="adm-page-stats">
          <div className="adm-stat adm-stat-blue-mauve">
            <span className="adm-stat-value">{page.mine.total.toLocaleString('en-US')}</span>
            <span className="adm-stat-label">mine</span>
          </div>
          <div className="adm-stat adm-stat-yellow">
            <span className="adm-stat-value">{page.counts.open.toLocaleString('en-US')}</span>
            <span className="adm-stat-label">in progress</span>
          </div>
          <div className="adm-stat adm-stat-green-mode">
            <span className="adm-stat-value">{page.counts.resolved.toLocaleString('en-US')}</span>
            <span className="adm-stat-label">resolved</span>
          </div>
        </div>
        <div className="adm-page-actions">
          {/*
            Butonul se vede INTOTDEAUNA, si cand categoria inca nu exista.
            Ascuns pana la o configurare, ecranul spune „nu se poate cere
            nimic" - iar omul care tocmai a citit ca aici se cer lucruri nu are
            ce face cu asta. Fara categorie, cererea pleaca la echipa
            produsului, si ecranul spune limpede ce s-a intamplat cu ea.
          */}
          <button
            type="button"
            className={`fr-new-btn${open ? ' is-on' : ''}`}
            id="frNew"
            onClick={() => setOpen((was) => !was)}
          >
            <i className="fas fa-plus" aria-hidden="true"></i>
            {words.neu}
          </button>
        </div>
      </div>

      <NewRequest page={page} words={words} open={open} onClose={() => setOpen(false)} onSent={reload} />

      {!page.ready ? (
        <NotSetUp page={page} onChanged={reload} />
      ) : page.requests.length === 0 ? (
        <div className="adm-card">
          <div className="adm-empty-inline">
            <i className="fa fa-lightbulb"></i>
            {` ${words.empty} Use the `}
            <strong>{words.neu}</strong>
            {' button, top right.'}
          </div>
        </div>
      ) : (
        <>
          <div className="fr-split">
            <div className="fr-main">
              <Group title="Waiting" note="still open - yours come first" rows={waiting} page={page} onChanged={reload} />
              <Group title="Resolved" note="done, newest first" rows={done} page={page} onChanged={reload} />
            </div>
            <Rail page={page} />
          </div>

          <p className="adm-muted fr-more">
            Everything is in the open, everyone&apos;s - yours come first. Each row is an ordinary ticket: the replies,
            the attachments and the history all live on it. Click a row to read it here; the ticket itself is one click
            further.
          </p>
        </>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------------------
 * FORMULARUL, PRIMUL LUCRU DIN PAGINA
 *
 * Era o fereastra care se deschidea peste ecran. Doua lucruri nu mergeau: o
 * fereastra nu poate tine o zona de poze fara sa devina ea insasi un ecran, si
 * cat era deschisa nu se mai vedea lista de dedesubt - adica exact locul unde
 * omul ar fi vazut ca cere ceva ce a cerut deja.
 * ------------------------------------------------------------------------ */

type Picture = { file: File; url: string }

function NewRequest({
  page,
  words,
  open,
  onClose,
  onSent,
}: {
  page: Page
  words: Words
  open: boolean
  onClose(): void
  onSent(): void
}) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [pictures, setPictures] = useState<Picture[]>([])
  const [shown, setShown] = useState(0)
  const [error, setError] = useState('')
  const [sending, setSending] = useState(false)
  const [live, setLive] = useState(false)
  const [over, setOver] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  /*
   * Panoul are DOUA stari, ca in `feature_requests.js`:
   *
   *  - `here` - e in pagina (fara `hidden`);
   *  - `live` - are `is-open`, adica a coborat.
   *
   * La deschidere iese INTAI din `hidden`, si abia in al doilea cadru primeste
   * `is-open`: puse in aceeasi clipa, browserul le socoteste impreuna si panoul
   * apare dintr-o data, fara coborare. La inchidere, invers: cade `is-open`, si
   * `hidden` asteapta sfarsitul coborarii inapoi (260 ms) - pus pe loc, ar taia
   * animatia in doua.
   */
  const [here, setHere] = useState(false)

  /* Lista de poze se citeste dintr-un `ref`, nu din `pictures`: pusa in
     dependintele efectului, ar goli panoul la fiecare poza adaugata. */
  const held = useRef<Picture[]>([])
  useEffect(() => {
    held.current = pictures
  }, [pictures])

  useEffect(() => {
    if (open) {
      /* Panoul deschis porneste gol, ca in portal (`reset()`). */
      setTitle('')
      setDescription('')
      setError('')
      held.current.forEach((one) => URL.revokeObjectURL(one.url))
      setPictures([])
      setShown(0)
      setHere(true)

      let inner = 0
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setLive(true))
      })

      return () => {
        cancelAnimationFrame(outer)
        cancelAnimationFrame(inner)
      }
    }

    setLive(false)
    const timer = setTimeout(() => setHere(false), 260)

    return () => clearTimeout(timer)
  }, [open])

  /* Pozele bune, in ordinea in care au venit. Un `input[type=file]` nu se poate
     umple din cod, deci lista e a noastra si pleaca prin `FormData`. */
  const addFiles = useCallback(
    (files: File[]) => {
      const room = Math.max(0, MAX_IMAGES - pictures.length)
      const good = files
        .filter((file) => IMAGE_TYPES.includes(file.type) && file.size <= MAX_BYTES)
        .slice(0, room)
        .map((file) => ({ file, url: URL.createObjectURL(file) }))

      if (good.length === 0) return

      setPictures([...pictures, ...good])
      setShown(pictures.length)
    },
    [pictures],
  )

  /* Ctrl+V cu o captura in clipboard, cat panoul e deschis - ca in portal. */
  useEffect(() => {
    if (!open) return
    const onPaste = (ev: ClipboardEvent) => {
      const items = ev.clipboardData?.items
      if (!items) return
      const found: File[] = []
      for (const item of items) {
        if (item.kind !== 'file') continue
        const file = item.getAsFile()
        if (file) found.push(file)
      }
      if (found.length > 0) {
        ev.preventDefault()
        addFiles(found)
      }
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
  }, [open, addFiles])

  async function send() {
    const trimmed = title.trim()
    if (trimmed === '') {
      setError('Write a short line about what this is.')
      return
    }

    setSending(true)
    setError('')

    try {
      const answer = await submitFeedback(
        { kind: page.kind, title: trimmed, description },
        pictures.map((one) => one.file),
      )
      newAlert(`<i class="fas fa-check-circle"></i> ${answer.message}`, 'success', 5000)
      onClose()
      onSent()
    } catch (why) {
      setError(why instanceof Error ? why.message : 'It did not work.')
    } finally {
      setSending(false)
    }
  }

  const picture = pictures[Math.min(shown, pictures.length - 1)]

  return (
    <section
      className={`fr-new${live ? ' is-open' : ''}`}
      id="frPanel"
      hidden={!here}
      aria-label={words.neu}
    >
      {/* Doua invelisuri, si fiecare are treaba lui: grila animeaza inaltimea
          de la 0fr la 1fr, iar `0fr` strange numai PRIMUL rand. Un singur copil
          sub grila, si captuseala pe cel dinauntru. */}
      <div className="fr-new-inner">
        <div className="fr-new-pad">
          <div className="fr-new-grid">
            <div className="fr-new-shots">
              <div
                className={`fr-drop${over ? ' is-over' : ''}`}
                id="frDrop"
                role="button"
                tabIndex={0}
                hidden={pictures.length > 0}
                onClick={() => fileInput.current?.click()}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter' || ev.key === ' ') fileInput.current?.click()
                }}
                onDragOver={(ev) => {
                  ev.preventDefault()
                  setOver(true)
                }}
                onDragLeave={() => setOver(false)}
                onDrop={(ev) => {
                  ev.preventDefault()
                  setOver(false)
                  addFiles(Array.from(ev.dataTransfer.files))
                }}
              >
                <i className="fas fa-image" aria-hidden="true"></i>
                <span className="fr-drop-main">Drop a screenshot here, or click to pick one</span>
                <span className="fr-drop-sub">PNG, JPEG, WEBP or GIF. You can also paste with Ctrl&nbsp;+&nbsp;V.</span>
                <input
                  ref={fileInput}
                  type="file"
                  className="fr-file"
                  id="frFiles"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  multiple
                  onChange={(ev) => {
                    addFiles(Array.from(ev.target.files ?? []))
                    ev.target.value = ''
                  }}
                />
              </div>

              <div className="fr-shots" id="frShots" hidden={pictures.length === 0}>
                {picture && (
                  <>
                    <div className="fr-big">
                      <img src={picture.url} alt="" />
                      <button
                        type="button"
                        className="fr-big-x"
                        title="Remove this picture"
                        onClick={() => {
                          const gone = pictures[shown]
                          if (gone) URL.revokeObjectURL(gone.url)
                          const left = pictures.filter((_, i) => i !== shown)
                          setPictures(left)
                          setShown(Math.max(0, Math.min(shown, left.length - 1)))
                        }}
                      >
                        &times;
                      </button>
                      {pictures.length > 1 && (
                        <>
                          <button
                            type="button"
                            className="fr-big-nav is-prev"
                            aria-label="Previous picture"
                            onClick={() => setShown((now) => (now - 1 + pictures.length) % pictures.length)}
                          >
                            &#8249;
                          </button>
                          <button
                            type="button"
                            className="fr-big-nav is-next"
                            aria-label="Next picture"
                            onClick={() => setShown((now) => (now + 1) % pictures.length)}
                          >
                            &#8250;
                          </button>
                          <span className="fr-big-count">{`${shown + 1} / ${pictures.length}`}</span>
                        </>
                      )}
                    </div>
                    <span className="fr-big-name">{picture.file.name}</span>
                    <div className="fr-strip">
                      {pictures.length > 1 &&
                        pictures.map((one, index) => (
                          <button
                            key={one.url}
                            type="button"
                            className={`fr-dot${index === shown ? ' is-on' : ''}`}
                            aria-label={`Picture ${index + 1}`}
                            onClick={() => setShown(index)}
                          />
                        ))}
                      {pictures.length < MAX_IMAGES && (
                        <button
                          type="button"
                          className="fr-add"
                          title="Add another picture"
                          onClick={() => fileInput.current?.click()}
                        >
                          +
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="fr-new-fields">
              <div className="form-group">
                <label htmlFor="frTitle">
                  {words.label} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="frTitle"
                  maxLength={190}
                  autoComplete="off"
                  placeholder={words.hint}
                  value={title}
                  onChange={(ev) => setTitle(ev.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="frDescription">{words.detail}</label>
                <textarea
                  className="form-control"
                  id="frDescription"
                  rows={5}
                  placeholder={words.holder}
                  value={description}
                  onChange={(ev) => setDescription(ev.target.value)}
                />
              </div>
            </div>
          </div>

          {page.ready ? (
            <p className="adm-muted fr-small">
              This opens a ticket in{' '}
              <strong>
                {page.categoryName} / {page.subcategoryName}
              </strong>
              , under your name. It shows up in the list below right away.
            </p>
          ) : (
            <p className="adm-muted fr-small">
              While the category and subcategories do not exist, the request does not become a ticket:{' '}
              <strong>it is saved here and sent to the product team</strong>. You will see it in the list below, with its
              status. Pictures travel with a ticket, so they wait for the categories too.
            </p>
          )}

          {error !== '' && <div className="alert alert-danger fr-error">{error}</div>}

          <div className="fr-new-foot">
            <button type="button" className="btn btn-primary" id="frSend" disabled={sending} onClick={() => void send()}>
              Send the request
            </button>
            <button type="button" className="btn btn-default" id="frCancel" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ---------------------------------------------------------------------------
 * FARA CATEGORIE
 * ------------------------------------------------------------------------ */

function NotSetUp({ page, onChanged }: { page: Page; onChanged(): void }) {
  return (
    <>
      <div className="alert alert-warning" style={{ margin: '20px' }}>
        <h4>
          <i className="fas fa-tags"></i> The categories are not set up yet
        </h4>
        <p>
          Product feedback is kept as tickets in an <strong>Optimum Desk</strong> category with two subcategories:{' '}
          <em>Feature Request</em> and <em>Bug Report</em>. It is set up once, under{' '}
          <strong>Settings &gt; Easeedesk &gt; Categories</strong>.
        </p>
        <p style={{ marginBottom: 0 }}>
          From that moment the screen just works - nothing else to do. Until then, what you write here is kept and sent
          on to the product team; the requests are below.
        </p>
      </div>

      {page.relayed.length > 0 && (
        <>
          <div className="fr-list">
            {page.relayed.map((one) => (
              <Relayed key={one.id} row={one} canOperate={page.canOperate} onChanged={onChanged} />
            ))}
          </div>

          <p className="adm-muted fr-more">
            These are not tickets yet - no number, no history. They become tickets as soon as the category exists; the
            ones here stay on record either way.
          </p>
        </>
      )}
    </>
  )
}

/**
 * O cerere scrisa acasa.
 *
 * BUTOANELE DE REINCERCARE. In portal ele stateau langa Change Log, in casutele
 * din `_partials/feedback_boxes.php`, si nu pe ecranul asta - dar capetele sunt
 * ale aceluiasi controller (`relayRetry`, `relayDismiss`). Aici stau langa
 * randul de care tin, ca sa fie de ajuns pentru cine tocmai a reparat capatul,
 * si numai pentru cine are voie sa opereze - `canOperate`, hotarat pe server.
 */
function Relayed({ row, canOperate, onChanged }: { row: RelayedRequest; canOperate: boolean; onChanged(): void }) {
  const [busy, setBusy] = useState(false)

  async function act(what: 'retry' | 'dismiss') {
    setBusy(true)
    try {
      const answer = what === 'retry' ? await retryRelay(row.id) : await dismissRelay(row.id)
      newAlert(`<i class="fas fa-check-circle"></i> ${answer.message}`, 'success', 4000)
      onChanged()
    } catch (why) {
      newAlert(why instanceof Error ? why.message : 'It did not work.', 'danger', 6000)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`adm-card fr-item${row.isMine ? ' fr-mine' : ''}`}>
      <div className="fr-body">
        <div className="fr-head">
          <h4>{row.subject}</h4>
          {row.sent ? (
            <span className="fr-pill fr-pill-sent">
              <i className="fas fa-paper-plane"></i> sent to the product team
            </span>
          ) : row.stuck ? (
            <span className="fr-pill fr-pill-stuck" title={row.lastError}>
              <i className="fas fa-exclamation-triangle"></i> could not be sent
            </span>
          ) : (
            <span className="fr-pill fr-pill-waiting" title={row.lastError}>
              <i className="far fa-clock"></i> saved, will be sent shortly
            </span>
          )}
          {row.isMine && <span className="fr-badge-mine">yours</span>}
        </div>

        {row.description.trim() !== '' && <p className="fr-desc">{short(row.description)}</p>}

        <div className="fr-meta">
          {!row.isMine && row.userName.trim() !== '' && (
            <span>
              <i className="fas fa-user"></i> {row.userName}
            </span>
          )}
          <span>
            <i className="far fa-clock"></i> asked {day(row.created)}
          </span>
          {canOperate && !row.sent && (
            <span className="fr-actions">
              <button type="button" className="btn btn-xs btn-default" disabled={busy} onClick={() => void act('retry')}>
                Try again now
              </button>
              {!row.stuck && (
                <button
                  type="button"
                  className="btn btn-xs btn-default"
                  disabled={busy}
                  onClick={() => void act('dismiss')}
                >
                  Stop retrying
                </button>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------------------
 * LISTA
 * ------------------------------------------------------------------------ */

function Group({
  title,
  note,
  rows,
  page,
  onChanged,
}: {
  title: string
  note: string
  rows: FeedbackRequest[]
  page: Page
  onChanged(): void
}) {
  if (rows.length === 0) return null

  return (
    <>
      <div className="fr-group-head">
        <span>{title}</span>
        <b>{rows.length}</b>
        <em>{note}</em>
      </div>
      <div className="fr-list">
        {rows.map((row) => (
          <Row key={row.id} row={row} page={page} onChanged={onChanged} />
        ))}
      </div>
    </>
  )
}

function Row({ row, page, onChanged }: { row: FeedbackRequest; page: Page; onChanged(): void }) {
  const [open, setOpen] = useState(false)
  const [moving, setMoving] = useState(false)
  const rowId = `fr${row.id}`

  async function move(status: number) {
    if (status < 1) return
    setMoving(true)
    try {
      const answer = await advanceFeedback(row.id, status)
      newAlert(`<i class="fas fa-check-circle"></i> ${answer.message}`, 'success', 4000)
      /* Pastila stadiului, lista urmatoarelor stadii si gruparea in „Waiting"
         sau „Resolved" se hotarasc la SERVER: se cere pagina din nou, nu se
         ghiceste aici. */
      onChanged()
    } catch (why) {
      newAlert(why instanceof Error ? why.message : 'The stage could not be changed.', 'danger', 9000)
    } finally {
      setMoving(false)
    }
  }

  return (
    <div className={`adm-card fr-item${row.isMine ? ' fr-mine' : ''}${open ? ' is-open' : ''}`} id={rowId}>
      <button
        type="button"
        className="fr-open"
        aria-expanded={open}
        aria-controls={`${rowId}-detail`}
        onClick={() => setOpen((was) => !was)}
      >
        <div className="fr-body">
          <div className="fr-head">
            <h4>{row.subject}</h4>
            {row.statusName !== '' && (
              <span
                className="fr-status"
                style={
                  row.statusColor === ''
                    ? undefined
                    : { background: `${row.statusColor}1f`, color: row.statusColor }
                }
              >
                {row.statusName}
              </span>
            )}
            {row.isMine && <span className="fr-badge-mine">yours</span>}
          </div>

          {row.description.trim() !== '' && <p className="fr-desc">{short(row.description)}</p>}

          <div className="fr-meta">
            <span className="fr-number">#{row.id}</span>
            {!row.isMine && row.authorName.trim() !== '' && (
              <span>
                <i className="fas fa-user"></i> {row.authorName}
              </span>
            )}
            <span>
              <i className="far fa-clock"></i> asked {day(row.created)}
            </span>
            {row.resolved ? (
              <span className="fr-done">
                <i className="fas fa-check"></i> resolved {day(row.resolved)}
              </span>
            ) : (
              row.updated && (
                <span title="Last activity on the ticket">
                  <i className="fas fa-history"></i> last activity {day(row.updated)}
                </span>
              )
            )}
          </div>
        </div>
        <span className="fr-chevron" aria-hidden="true">
          <i className="fas fa-chevron-right"></i>
        </span>
      </button>

      <div className="fr-detail" id={`${rowId}-detail`} hidden={!open}>
        <div className={`fr-detail-grid${row.shots.length > 0 ? '' : ' fr-detail-noshots'}`}>
          {/* POZELE, IN STANGA: o cerere despre un ecran vine aproape mereu cu
              o captura, si aia e jumatate din explicatie - se uita la ea INAINTE
              sa citeasca. */}
          {row.shots.length > 0 && (
            <div className="fr-shots">
              {row.shots.map((shot) => (
                <Shot key={shot.filename} shot={shot} />
              ))}
            </div>
          )}

          <div className="fr-detail-main">
            {row.description.trim() !== '' ? (
              <p className="fr-detail-text">{row.description}</p>
            ) : (
              <p className="fr-detail-text adm-muted">No description was written.</p>
            )}

            <div className="fr-detail-facts">
              <span>
                <strong>Asked by</strong> {row.authorName.trim() !== '' ? row.authorName : row.isMine ? 'you' : '—'}
              </span>
              <span>
                <strong>Asked</strong> {moment(row.created)}
              </span>
              {row.updated && (
                <span>
                  <strong>Last activity</strong> {moment(row.updated)}
                </span>
              )}
              {row.resolved && (
                <span>
                  <strong>Resolved</strong> {moment(row.resolved)}
                </span>
              )}
              {row.statusName !== '' && (
                <span>
                  <strong>Stage</strong> {row.statusName}
                </span>
              )}
            </div>

            {row.files.length > 0 && (
              <ul className="fr-files">
                {row.files.map((file) => (
                  <li key={file.filename}>
                    <a href={file.url}>
                      <i className="fas fa-paperclip" aria-hidden="true"></i> {file.filename}
                    </a>
                    <em>{bytes(file.size)}</em>
                  </li>
                ))}
              </ul>
            )}

            {row.comments.length > 0 && (
              <div className="fr-talk">
                <span className="fr-talk-head">Replies</span>
                {row.comments.map((said) => (
                  <div className="fr-said" key={said.id}>
                    <div className="fr-said-who">
                      <b>{said.author.trim() !== '' ? said.author : 'Support'}</b>
                      <span>{moment(said.created)}</span>
                    </div>
                    {/*
                      Comentariile se scriu prin editor si se pastreaza cu
                      formatarea lor, ca in portal, unde macheta le lipea in
                      pagina asa cum sunt. Curatarea e a lor, pe drumul pe care
                      se scriu - vezi CLAUDE.md; ce se vede si ce nu hotaraste
                      serverul (`commentVisibility`), nu componenta asta.
                    */}
                    <div className="fr-said-text" dangerouslySetInnerHTML={{ __html: said.comment }} />
                  </div>
                ))}
              </div>
            )}

            <div className="fr-detail-acts">
              {/*
                STADIUL URMATOR, DE AICI. Stadiile oferite sunt chiar cele pe
                care le ingaduie fluxul de unde e tichetul acum, iar mutarea
                merge pe drumul obisnuit al tichetelor - cu validarile,
                evenimentele si instiintarile lui.
              */}
              {page.canOperate && row.nextStatuses.length > 0 && (
                <span className="fr-next">
                  <label htmlFor={`${rowId}-next`}>Move to</label>
                  <select
                    className="fr-next-pick"
                    id={`${rowId}-next`}
                    value=""
                    disabled={moving}
                    onChange={(ev) => void move(Number(ev.target.value))}
                  >
                    <option value="">choose a stage...</option>
                    {row.nextStatuses.map((step) => (
                      <option key={step.id} value={step.id}>
                        {step.name}
                      </option>
                    ))}
                  </select>
                </span>
              )}

              {row.canOpen && (
                <a className="btn btn-xs btn-default fr-detail-open" href={`/app/easeedesk/ticket/${row.id}`}>
                  Open the ticket <i className="fas fa-arrow-right"></i>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------------------
 * CIFRE SCRISE, NU DESENATE
 *
 * Aici au fost o bara de stadii, un grafic cu bare de sapte pixeli si o lista
 * numita „At a glance". Ca sa afli ceva din ele trebuia sa le descifrezi. Acum
 * scrie in cuvinte, cu numerele la vedere.
 * ------------------------------------------------------------------------ */

function Rail({ page }: { page: Page }) {
  const all = page.counts.total
  const resolved = page.counts.resolved
  const queue = Math.max(0, all - resolved)
  const peak = page.effort.monthly.reduce((most, one) => Math.max(most, one.asked, one.resolved), 0)

  return (
    <aside className="fr-rail" aria-label="How these requests are going">
      <div className="fr-rail-box">
        <h6>{page.kind === 'bug' ? 'How the bug reports are going' : 'How the requests are going'}</h6>

        <p className="fr-say">
          {all === 0 ? (
            'Nothing has been asked yet.'
          ) : (
            <>
              <b>{all.toLocaleString('en-US')}</b> {all === 1 ? 'request' : 'requests'} so far
              {page.mine.total > 0 ? (
                <>
                  {' - '}
                  <b>{page.mine.total.toLocaleString('en-US')}</b> of them yours.
                </>
              ) : (
                ' - none of them yours yet.'
              )}
            </>
          )}
        </p>

        {all > 0 && (
          <>
            <p className="fr-say">
              {queue === 0 ? (
                'All of them are done.'
              ) : (
                <>
                  <b>{queue.toLocaleString('en-US')}</b> still waiting, <b>{resolved.toLocaleString('en-US')}</b> done.
                  {page.effort.oldestOpenHours !== null && (
                    <> The one waiting longest has been open <b>{span(page.effort.oldestOpenHours)}</b>.</>
                  )}
                </>
              )}
            </p>

            {page.effort.avgHours !== null && (
              <p className="fr-say">
                What gets done takes <b>{span(page.effort.avgHours)}</b> on average, from asking to resolved.
              </p>
            )}
          </>
        )}
      </div>

      {peak > 0 && (
        <div className="fr-rail-box">
          <h6>Month by month</h6>
          <table className="fr-months-table">
            <thead>
              <tr>
                <th>&nbsp;</th>
                <th>asked</th>
                <th>resolved</th>
              </tr>
            </thead>
            <tbody>
              {page.effort.monthly.map((month) => (
                <tr key={month.ym} className={month.asked === 0 && month.resolved === 0 ? 'is-quiet' : undefined}>
                  <td>{month.label}</td>
                  <td>{month.asked}</td>
                  <td>{month.resolved}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </aside>
  )
}
